// AGENT-DASH-LABELS: column-header humanization + date formatting.
//
// The backend ships pivot columns named with raw DAX / queryRef tokens like
// `IA_QuotesIssued_Dynamic` or `Calculated Total (Total Revenue for Total
// Quantity after Discount) YoY%`. Showing those verbatim in the table is
// hostile to users — this module is the single source of truth for the
// mapping from raw column name → friendly display label, and for the
// MM/DD/YYYY date format the rebuild standardised on.
//
// Currency / percent / count formatters live in MetricsTable.tsx and are
// not touched here — this module only owns headers + dates.

/* ------------------------------------------------------------------------- *
 * Friendly-name map. Keys are raw column names exactly as emitted by
 * `_evaluate_visual`. The match is case-sensitive on the raw string; an
 * additional case-insensitive fallback handles the common variants.
 * ------------------------------------------------------------------------- */

const DISPLAY_NAME_MAP: Record<string, string> = {
  // Quotes / orders measures.
  IA_QuotesIssued_Dynamic: "Quotes Issued",
  IA_QuotesConverted_Dynamic: "Quotes Won",
  IA_QuotesWon_Dynamic: "Quotes Won",
  IA_OrdersReleased_Dynamic: "Orders Released",
  IA_OrdersReleased: "Orders Released",
  IA_OrdersReleased_12Mo: "Orders Released (12 Mo)",
  IA_OrdersUnreleased_Dynamic: "Unreleased Orders",
  IA_ConversionRate_Dynamic: "Conversion Rate",
  IA_QtyQuoted_Dynamic: "Qty Quoted",
  IA_QtySold_Dynamic: "Qty Sold",
  IA_QtyCloseRate_Dynamic: "Close Rate",
  IA_QtySoldRatio_Dynamic: "Sold Ratio",

  // Revenue (PBI emits a few different long-form names for the same measure).
  "Calculated Total": "Revenue",
  "Calculated Total (Total Revenue for Total Quantity after Discount)": "Revenue",
  "Calculated Total (Total Revenue for Total Quantity after Discount) YoY%": "Revenue YoY %",
  "Calculated Total YoY%": "Revenue YoY %",

  // Product taxonomy.
  PP_Display: "Parent Product",
  PARENT_PRODUCT_LINE_NAME: "Parent Product",
  CATEGORY_PRODUCT_PRI_NAME: "Category",

  // Item-level price / cost.
  TUI_SELL: "Sell Price",
  COSTBUY: "Cost",
  REVENUE_12MO: "Revenue (12 Mo)",
  REVENUE_6MO: "Revenue (6 Mo)",
  ORDER_TOTAL_TNI: "Order Total",
  Total_TI: "Total (Inc Tax)",
  Total_TNI: "Total",

  // Date columns.
  INVOICE_DATE: "Invoice Date",
  INVOICEDATE: "Invoice Date",
  ENTRYDATE: "Entry Date",
  ENTRY_DATE: "Entry Date",
  RELEASED_DATE: "Released Date",
  LAST_ORDER_DATE: "Last Order",
  LAST_SALE_DATE: "Last Sale",
  LAST_QUOTED: "Last Quoted",
  LAST_SOLD: "Last Sold",
  DISCONTINUED_DATE: "Discontinued",

  // Calendar tokens. AGENT-C4: the HSQ pivot now also emits a plain "Year"
  // column derived from RELEASED_DATE (see pages.py _C4_YEAR_OUT_NAME). The
  // explicit identity entry keeps it under the friendly-name path instead of
  // routing through the generic humanizer (which would Title-Case it the
  // same way, but this is the canonical mapping site).
  Year: "Year",
  "Date Year": "Year",
  "Calendar Year": "Year",
  "Calendar Quarter": "Quarter",

  // B2: PBI "by Parent Product" pivot money / win columns. Backend emits
  // these raw names via the visual's nativeQueryRef (see pages.json visual
  // 0e4039130424e90e0345). Keep display labels short to match the screenshot
  // — the screenshot uses "Won %", "$ Quoted", "$ Won", "$ per Day Quoted",
  // "$ per Day Won" verbatim, and "Active" for the active-quote count.
  Active: "Active",
  "Won %": "Won %",
  "$ Quoted": "$ Quoted",
  "$ Won": "$ Won",
  "$ Won per Day": "$ per Day Won",
  "$ Quoted per Day": "$ per Day Quoted",
  "$ per Day Won": "$ per Day Won",
  "$ per Day Quoted": "$ per Day Quoted",

  // AGENT-B1: the per-product pivot emits these raw names because the
  // measures' queryRefs in pages.json don't carry `_Dynamic` (they were
  // renamed mid-rebuild to match the synth-column tails the override path
  // resolves against Product Table). Prior to this entry the generic
  // humanizer would render e.g. `Item Analysis.IA_WonRate` as the awkward
  // "Item Analysis IA Won Rate" — `humanizeColumnName` below now strips the
  // `Item Analysis.` / `Product Table.` prefix before looking up the map
  // so the bare column names below match.
  IA_QuotesIssued: "Quotes Issued",
  IA_QuotesConverted: "Quotes Won",
  IA_QuotesConverted_12Mo: "Quotes Won (12 Mo)",
  IA_QuotesWon: "Quotes Won",
  IA_WonRate: "Conversion Rate",
  IA_CloseRate: "Close Rate",
  IA_ConversionRate: "Conversion Rate",
  IA_ConversionRate_12Mo: "Conversion Rate (12 Mo)",
  IA_QtyQuoted: "Qty Quoted",
  IA_QtyQuoted_12Mo: "Qty Quoted (12 Mo)",
  IA_QtySold: "Qty Sold",
  IA_QtySold_12Mo: "Qty Sold (12 Mo)",
  IA_QtyCloseRate: "Close Rate (Qty)",
  IA_QtyCloseRate_12Mo: "Close Rate (Qty, 12 Mo)",
  IA_QuotesIssued_12Mo: "Quotes Issued (12 Mo)",
  IA_QuotesWon_12Mo_X: "Quotes Won (12 Mo)",
};

// AGENT-B1: prefixes the visual evaluator's `out_name` carries when the
// queryRef wasn't given a `nativeQueryRef`. Stripping these in
// `humanizeColumnName` lets the bare-column entries above match (e.g.
// `Item Analysis.IA_WonRate` → "Win Rate" via "IA_WonRate" in the map).
const STRIPPABLE_PREFIXES = ["Item Analysis.", "Product Table.", "Invoice."];

// Case-insensitive secondary lookup so PBI-emitted casing variants
// (e.g. `pp_display`, `INVOICEDATE` vs `InvoiceDate`) still resolve.
const DISPLAY_NAME_MAP_CI: Record<string, string> = Object.fromEntries(
  Object.entries(DISPLAY_NAME_MAP).map(([k, v]) => [k.toLowerCase(), v]),
);

/* ------------------------------------------------------------------------- *
 * Generic fallback humaniser — kebab / snake / camel → Title Case.
 * ------------------------------------------------------------------------- */

function genericHumanize(raw: string): string {
  if (!raw) return raw;
  // Strip trailing measure suffixes that leak from PBI.
  let s = raw.replace(/_Dynamic$/i, "");

  // Split snake_case / kebab-case / dot-separated.
  s = s.replace(/[_\-.]+/g, " ");

  // Split camelCase / PascalCase + acronym boundaries (e.g. "quoteId" →
  // "quote Id", "HTTPResponseCode" → "HTTP Response Code").
  s = s.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  s = s.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");

  // Collapse whitespace.
  s = s.replace(/\s+/g, " ").trim();

  // Title-case each word, preserving short acronyms (2-3 uppercase letters)
  // and pure-uppercase tokens.
  return s
    .split(" ")
    .map((w) => {
      if (!w) return w;
      // Pure-uppercase short token? keep as-is (e.g. "ID", "PBI", "TUI").
      if (/^[A-Z0-9]{2,4}$/.test(w)) return w;
      // Standard title case.
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Convert a raw column name into its user-facing display label.
 *
 * 1. Exact map hit (case-sensitive).
 * 2. Case-insensitive map hit.
 * 3. AGENT-B1: strip a leading `Item Analysis.` / `Product Table.` /
 *    `Invoice.` prefix and re-try the map (covers the pivot evaluator's
 *    `out_name = queryRef` fallback when the spec didn't carry a
 *    `nativeQueryRef`).
 * 4. Generic snake/kebab/camel-to-title fallback.
 */
export function humanizeColumnName(raw: string): string {
  if (!raw) return raw;
  const direct = DISPLAY_NAME_MAP[raw];
  if (direct) return direct;
  const ci = DISPLAY_NAME_MAP_CI[raw.toLowerCase()];
  if (ci) return ci;
  // AGENT-B1: strip a recognised table-name prefix and retry.
  for (const prefix of STRIPPABLE_PREFIXES) {
    if (raw.startsWith(prefix)) {
      const tail = raw.slice(prefix.length);
      const tailDirect = DISPLAY_NAME_MAP[tail];
      if (tailDirect) return tailDirect;
      const tailCi = DISPLAY_NAME_MAP_CI[tail.toLowerCase()];
      if (tailCi) return tailCi;
      // No mapped hit on the tail — fall through to generic on the
      // unprefixed tail so the user sees a clean label.
      return genericHumanize(tail);
    }
  }
  return genericHumanize(raw);
}

/* ------------------------------------------------------------------------- *
 * Date detection + formatting.
 * ------------------------------------------------------------------------- */

// Date-like display-label suffixes. Only matches labels ending in a bare
// "Date" word or one of the "Last <X>" patterns (Last Quoted / Last Sold /
// Last Order / Last Sale). The previous broad pattern matched plain
// "Qty Quoted" / "Qty Sold" / "Online Order" labels, so integer measures
// were misread as epoch-millisecond dates and rendered as 12/31/1969.
const DATE_SUFFIX_RX = /(?:\bDate|\bLast\s+(?:Quoted|Sold|Order|Sale))$/;

// Raw-name fallback patterns — catches columns whose mapped label doesn't
// match the suffix list but whose raw name is obviously a date.
const RAW_DATE_RX = /(DATE|LASTQUOTED|LASTSOLD|LAST_QUOTED|LAST_SOLD)/i;

/**
 * Whether a column should be rendered as a date.
 *
 * Combines raw-name patterns, the humanized display name's suffix, and the
 * pandas dtype (datetime64 family) so we don't accidentally format an
 * arbitrary numeric column as a date.
 */
export function isDateLike(
  rawName: string,
  displayName: string,
  dtype?: string,
): boolean {
  const dt = (dtype || "").toLowerCase();
  // Explicit datetime dtype is the strongest signal.
  if (dt.startsWith("datetime") || dt === "date" || dt === "timestamp") {
    return true;
  }
  // Display-label suffix.
  if (displayName && DATE_SUFFIX_RX.test(displayName)) return true;
  // Raw-name fallback.
  if (rawName && RAW_DATE_RX.test(rawName)) return true;
  return false;
}

/**
 * Format a value as MM/DD/YYYY. Null / NaT / 1970-01-01 / empty → em-dash.
 *
 * Accepts ISO strings (`2026-05-19T00:00:00`), JS Dates, epoch numbers, and
 * other parseable strings. Anything that fails Date parsing is returned via
 * String() so we don't silently swallow weird inputs (e.g. an int that
 * sneaks into a date column).
 */
export function formatDateMMDDYYYY(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed.toLowerCase() === "nat") return "—";
    // 1970-01-01 sentinel (any time-of-day on the epoch) → treat as null.
    if (/^1970-01-01([T\s]00:00:00(\.000)?Z?)?$/.test(trimmed)) return "—";
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "—";
    return formatDateObj(value);
  }
  const d = new Date(value as string | number);
  if (Number.isNaN(d.getTime())) return String(value);
  // Epoch zero (numeric 0) is also a "null" sentinel.
  if (d.getTime() === 0) return "—";
  return formatDateObj(d);
}

function formatDateObj(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
}

/* ------------------------------------------------------------------------- *
 * Year column — hard guard against date formatting.
 *
 * The HSQ pivot emits a plain "Year" / "Date Year" / "Calendar Year" column
 * derived from RELEASED_DATE (see pages.py _C4_YEAR_OUT_NAME). Its raw name
 * trips both the DATE_SUFFIX_RX (display name "Year" doesn't, but "Date Year"
 * does via "Date") and the RAW_DATE_RX (raw name "Date Year" / "Calendar
 * Year" contains "DATE"), so without this guard the cell renderer treats the
 * integer 2026 as an epoch-seconds value and prints `12/31/1969`.
 *
 * `isYearColumn` returns true for the three known year column names so the
 * renderer can branch to `formatYear` instead of `formatDateMMDDYYYY`.
 * `formatYear` returns the integer year (1900..2100) or em-dash for
 * null / 0 / out-of-range sentinels.
 * ------------------------------------------------------------------------- */

export function isYearColumn(rawName: string, displayName: string): boolean {
  const n = (displayName || rawName || "").trim().toLowerCase();
  return n === "year" || n === "date year" || n === "calendar year";
}

export function formatYear(value: unknown): string {
  if (value === null || value === undefined || value === "" || value === 0) {
    return "—";
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1900 || n > 2100) return "—";
  return String(Math.round(n));
}
